import React, { useCallback, useMemo } from "react";
import type { WinRatioChartProps, WinRatioTrendItem } from "../../type";
import { colors } from "../../../../utils/colors";
import { useApp } from "../../../../contexts/AppContext";
import {
  GenericBarChart,
  type GenericBarChartRow,
} from "../GenericBarChart";
import {
  EMPTY_WIN_RATIO_STATE_MESSAGE,
  MONTH_SHORT,
  WIN_RATIO_SERIES_NAME,
} from "../../../../utils/consts";
import { winRatioChartConfig } from "./styles/winLossAnalysisStyles";

const toChartRows = (series: WinRatioTrendItem[]): GenericBarChartRow[] =>
  series.map((item) => ({
    label: `${MONTH_SHORT[item.month - 1] ?? item.month} ${item.year}`,
    value: Number(item.win_ratio),
  }));

const formatPercent = (value: number) => `${Number(value).toFixed(2)}%`;
const formatYAxisPercent = (value: number) => `${Number(value).toFixed(1)}%`;

export const WinRatioChart: React.FC<WinRatioChartProps> = ({
  title = "Win Ratio",
  loading = false,
  series,
}) => {
  const { drawerOpen } = useApp();
  const rows = useMemo(() => toChartRows(series), [series]);

  const axisTooltipFormatter = useCallback(
    ({
      categoryLabel,
      primaryValue,
      seriesMarkerHtml,
    }: {
      categoryLabel: string;
      primaryValue: number;
      seriesMarkerHtml: string;
    }) =>
      `${categoryLabel}<br/>${seriesMarkerHtml} ${WIN_RATIO_SERIES_NAME}: ${formatPercent(primaryValue)}`,
    [],
  );

  return (
    <GenericBarChart
      title={title}
      titleAlign={winRatioChartConfig.titleAlign}
      data={rows}
      seriesName={WIN_RATIO_SERIES_NAME}
      loading={loading}
      height={winRatioChartConfig.height}
      minHeight={winRatioChartConfig.minHeight}
      emptyMessage={EMPTY_WIN_RATIO_STATE_MESSAGE}
      legendTop={winRatioChartConfig.legendTop}
      legendRight={winRatioChartConfig.legendRight}
      barWidth={winRatioChartConfig.barWidth}
      xAxisLabelColor={colors.black}
      xAxisLabelFontSize="0.625rem"
      xAxisLabelFontWeight={500}
      yAxisLabelColor={colors.chart_gray}
      yAxisLabelFontSize="0.6875rem"
      xAxisRotateThreshold={winRatioChartConfig.xAxisRotateThreshold}
      defaultBarColor={colors.chart_blue}
      tooltipValueFormatter={formatPercent}
      axisTooltipFormatter={axisTooltipFormatter}
      yAxisValueFormatter={formatYAxisPercent}
      resizeTrigger={drawerOpen}
    />
  );
};
